import { IncludedTax, NonIncludedTax, TaxType } from '@kouto/cart-prices';
import { Divider } from 'components/common/styled/common-styled';
import { useBrandCurrency } from 'hooks/useBrandCurrency';
import usePoints from 'hooks/usePoints';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { currencyFormat } from 'utils';
import { CreditCode, CreditCodes } from 'types/credit-codes';
import {
  CalculationRow,
  PriceSectionContainer,
  CreditCodeSection,
} from './styles';
import CreditCodeItem from './CreditCodeItem';

interface PriceSectionProps {
  cartTotal: number;
  cartSubTotal: number;
  creditCodes?: CreditCodes['creditCodes'];
  isPayingWithPoints?: boolean;
  includedTaxes: IncludedTax[];
  nonIncludedTaxes: NonIncludedTax[];
  removeCreditCode?: (code: string) => void;
}

const PriceSection = ({
  cartTotal,
  cartSubTotal,
  creditCodes,
  includedTaxes,
  isPayingWithPoints,
  nonIncludedTaxes,
  removeCreditCode,
}: PriceSectionProps) => {
  const currency = useBrandCurrency();
  const { t } = useTranslation();

  const subTotalWithTaxes = nonIncludedTaxes.reduce((acc, tax) => {
    if (tax.type === TaxType.PERCENTAGE) {
      return acc + (cartSubTotal + tax.amountFee);
    }
    return acc;
  }, 0);
  const { count: cartSubTotalPoints } = usePoints(
    isPayingWithPoints ? subTotalWithTaxes : cartSubTotal,
  );

  if (cartTotal === 0) {
    return null;
  }

  return (
    <PriceSectionContainer>
      <CalculationRow className="cart-summary-subtotal">
        <span>
          {t('subTotal')}:{includedTaxes.length > 0 && ' *'}
        </span>
        {isPayingWithPoints ? (
          <span>{t('pointsWithCount', { count: cartSubTotalPoints })}</span>
        ) : (
          <span>{currencyFormat(currency)(cartSubTotal)}</span>
        )}
      </CalculationRow>
      {!isPayingWithPoints && (
        <>
          {nonIncludedTaxes.map((tax) => (
            <CalculationRow key={tax.name} className="cart-summary-tax">
              <span>{tax.name}</span>
              <span>{currencyFormat(currency)(tax.amountFee)}</span>
            </CalculationRow>
          ))}
        </>
      )}
      {creditCodes && creditCodes.length > 0 && (
        <>
          <Divider space={20} />
          <CreditCodeSection>
            {creditCodes.map((creditCode: CreditCode) => (
              <CreditCodeItem
                key={creditCode.code}
                creditCode={creditCode}
                totalCartDiscount={creditCode.value}
                removeCreditCode={removeCreditCode}
                isPayingWithPoints={isPayingWithPoints}
              />
            ))}
          </CreditCodeSection>
        </>
      )}
    </PriceSectionContainer>
  );
};

export default PriceSection;
